import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Chip,
  InputAdornment,
  TextField,
  Breadcrumbs,
  Link,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useNotify } from '../contexts/FeedbackContext';
import Pagination from '../components/common/Pagination';

const VARIANT_CONFIG = {
  retail: {
    flagField: 'is_available_for_retail',
    priceField: 'price_per_kg',
    tagLabel: 'Minorista',
    sectionName: 'Pedidos al público',
    title: 'Productos Minorista',
    subtitle: 'Productos disponibles para venta al público. Se etiquetan desde Productos (Tango).',
    tagColor: { bg: '#e3f2fd', color: '#1976d2', border: '#90caf9' },
  },
  wholesale: {
    flagField: 'is_available_for_wholesale',
    priceField: 'wholesale_price_per_kg',
    tagLabel: 'Mayorista',
    sectionName: 'Pedidos mayoristas',
    title: 'Productos Mayorista',
    subtitle: 'Productos disponibles para comercios mayoristas. Se etiquetan desde Productos (Tango).',
    tagColor: { bg: '#fff3e0', color: '#f57c00', border: '#ffb74d' },
  },
};

const SectionProducts = ({ variant = 'retail' }) => {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.retail;
  const { flagField, priceField, tagLabel } = config;

  const navigate = useNavigate();
  const { logout } = useAuth();
  const notify = useNotify();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    setPage(0);
    setSearchTerm('');
  }, [variant]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchProducts();
    }, 300);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, variant]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = [
        `page=${page + 1}`,
        'is_active=true',
        `${flagField}=true`,
        searchTerm ? `search=${encodeURIComponent(searchTerm)}` : '',
      ].filter(Boolean).join('&');

      const response = await axios.get(`/api/products/?${params}`);
      if (response.data && response.data.results) {
        setProducts(response.data.results);
        setTotalCount(response.data.count);
      } else {
        setProducts(response.data || []);
        setTotalCount(response.data ? response.data.length : 0);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      notify('No se pudieron cargar los productos. Revisá la conexión e intentá de nuevo.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatPrice = (product) => {
    const value = parseFloat(product[priceField] || 0);
    return `$${value.toLocaleString('es-AR')}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            sx={{ color: '#4e7397', fontSize: '0.875rem' }}
          >
            Inicio
          </Link>
          <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>{config.sectionName}</Typography>
          <Typography sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>Productos</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', mb: 4, gap: 2 }}>
          <Box>
            <Typography
              variant="h3"
              sx={{ color: '#0e141b', fontSize: { xs: '1.875rem', md: '2.25rem' }, fontWeight: 900, mb: 1, letterSpacing: '-0.033em' }}
            >
              {config.title}
            </Typography>
            <Typography sx={{ color: '#4e7397', fontSize: '1rem' }}>{config.subtitle}</Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/products')}
            sx={{
              borderColor: '#800020', color: '#800020', alignSelf: { xs: 'stretch', md: 'center' },
              '&:hover': { borderColor: '#600010', bgcolor: 'rgba(128,0,32,0.04)' },
              borderRadius: '0.5rem', px: 3, py: 1.25, fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap',
            }}
          >
            Gestionar etiquetas en Tango
          </Button>
        </Box>

        {/* Products count */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
            <span style={{ fontWeight: 700, color: '#0e141b' }}>{totalCount}</span> productos {tagLabel.toLowerCase()}
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            placeholder="Buscar producto..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon sx={{ color: '#4e7397' }} /></InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton aria-label="clear search" onClick={() => { setSearchTerm(''); setPage(0); }} edge="end" size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%', maxWidth: '400px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.5rem', backgroundColor: '#f6f7f8',
                '& fieldset': { borderColor: '#d0dbe7' },
                '&:hover fieldset': { borderColor: '#800020' },
                '&.Mui-focused fieldset': { borderColor: '#800020' },
              },
            }}
          />
        </Box>

        {/* Desktop Table */}
        <TableContainer
          component={Paper}
          sx={{ display: { xs: 'none', md: 'block' }, borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none', overflow: 'hidden' }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f6f7f8' }}>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', py: 2 }}>Código / SKU</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Producto</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Categoría</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Precio {tagLabel}</TableCell>
                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Stock Tango</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>Cargando...</TableCell></TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ color: '#4e7397', mb: 1 }}>No hay productos etiquetados como {tagLabel}.</Typography>
                    <Button size="small" onClick={() => navigate('/products')} sx={{ color: '#800020', fontWeight: 700, textTransform: 'none' }}>
                      Etiquetar productos en Tango →
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow key={product.id} sx={{ '&:hover': { bgcolor: '#f6f7f8' }, '& .MuiTableCell-root': { borderColor: '#f0f0f0' } }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, color: '#0e141b', fontSize: '0.8125rem', fontFamily: 'monospace' }}>
                        {product.cod_articulo || `PG-${product.id}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box>
                          <Typography sx={{ color: '#0e141b', fontWeight: 700, fontSize: '0.875rem' }}>{product.name}</Typography>
                          <Typography sx={{ color: '#4e7397', fontSize: '0.75rem', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.description}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.category_name && <Chip label={product.category_name} size="small" sx={{ bgcolor: '#f5f5f5', color: '#616161', fontWeight: 700, border: '1px solid #bdbdbd' }} />}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#0e141b', fontWeight: 700, fontSize: '0.875rem' }}>
                        {formatPrice(product)} <span style={{ fontSize: '0.7rem', fontWeight: 500, color: '#4e7397' }}>/ {product.unit_display?.toLowerCase() || 'kg'}</span>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#0e141b', fontWeight: 700, fontSize: '0.8125rem' }}>
                        {product.tango_info ? product.tango_info.total_stock : 0} {product.unit_display || 'kg'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mobile Cards */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : products.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '1rem', border: '1px solid #d0dbe7' }}>
              <Typography sx={{ color: '#4e7397', mb: 1 }}>No hay productos etiquetados como {tagLabel}.</Typography>
              <Button size="small" onClick={() => navigate('/products')} sx={{ color: '#800020', fontWeight: 700, textTransform: 'none' }}>
                Etiquetar productos en Tango →
              </Button>
            </Paper>
          ) : (
            products.map((product) => (
              <Paper key={product.id} sx={{ p: 2, borderRadius: '1rem', border: '1px solid #d0dbe7', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.9375rem', color: '#0e141b' }}>{product.name}</Typography>
                  {product.category_name && <Chip label={product.category_name} size="small" sx={{ bgcolor: '#f5f5f5', color: '#616161', fontWeight: 700, fontSize: '0.65rem', border: '1px solid #bdbdbd', my: 0.5 }} />}
                  <Typography sx={{ fontWeight: 900, color: '#1b5e20', fontSize: '1rem' }}>
                    {formatPrice(product)} <span style={{ fontSize: '0.7rem', fontWeight: 500 }}>/ {product.unit_display?.toLowerCase() || 'kg'}</span>
                  </Typography>
                </Box>
              </Paper>
            ))
          )}
        </Box>

        {/* Pagination */}
        {totalCount > 0 && (
          <Pagination count={totalCount} page={page} rowsPerPage={20} onPageChange={(e, newPage) => setPage(newPage)} />
        )}
      </Box>
    </Box>
  );
};

export default SectionProducts;
